/**
 * Platformer config — the structured per-game data the SkinResolver consumes.
 * See data/frogger.config.js for the shape rationale.
 *
 *   grid     : board geometry (cell size, columns, rows)
 *   tuning   : per-mechanic magic numbers (gravity, jumpVelocity, …)
 *   modes    : mode definitions (id + label + overrides)
 *   settings : { key: { default, type, options?, label } } — defaults + OptionsScene UI metadata
 *   elements : asset slots { key: { label, footprint, defaultPath?, fallbackColor?, ... } }
 */

import {
    PLATFORMER_GRID,
    PLATFORMER_PHYSICS,
    PLATFORMER_PLAYER,
    PLATFORMER_SCORING,
    PLATFORMER_TILE_DEFS,
    PLATFORMER_ENTITY_DEFS,
} from '../src/games/platformer/platformerConfig.js';

/**
 * Shipped default art — the "Rama Butter" theme generated for the
 * brand-mockup replica stage (data/stages/platformer/rama01.json).
 * Keys are element slot keys (no 'platformer.' prefix); any slot a
 * user re-skins via the Options/Content Browser still wins over
 * these (see SkinResolver._resolveSrc override order).
 */
const RAMA_ART = {
    // Parallax stack: bg.2 = far painted backdrop (hills, milk-bottle
    // tower, windmill), bg.1 = drifting cloud band above it. Both are
    // TilingSprites sized to the stage height, so they tile cleanly on
    // any stage width (the per-stage `background` cover image would
    // zoom/crop badly on very wide boards).
    'bg.2':                 'assets/platformer/rama/rama-bg-far.png',
    'bg.1':                 'assets/platformer/rama/rama-bg-clouds.png',
    'autotile.ground':      'assets/platformer/rama/rama-autotile-ground.png',
    'tile.brick':           'assets/platformer/rama/rama-tile-brick.png',
    'tile.block.question':  'assets/platformer/rama/rama-block-question.png',
    'tile.block.hard':      'assets/platformer/rama/rama-block-crate.png',
    'entity.player.spawn':  'assets/platformer/rama/rama-player.png',
    'entity.goomba':        'assets/platformer/rama/rama-enemy-jam.png',
    'entity.koopa':         'assets/platformer/rama/rama-enemy-hen.png',
    'entity.coin':          'assets/platformer/rama/rama-coin.png',
    'entity.mushroom':      'assets/platformer/rama/rama-powerup-tub.png',
    'entity.flag.goal':     'assets/platformer/rama/rama-flag-goal-tall.png',
    'entity.checkpoint':    'assets/platformer/rama/rama-checkpoint-bread.png',
    'entity.platform.moving':'assets/platformer/rama/rama-platform-moving.png',
    'decor.castle':         'assets/platformer/rama/rama-decor-house.png',
    'decor.ladder':         'assets/platformer/rama/rama-decor-ladder-tile.png',
    'decor.sign':           'assets/platformer/rama/rama-decor-sign.png',
    'decor.bush.1':         'assets/platformer/rama/rama-decor-flowers.png',
    'decor.bush.2':         'assets/platformer/rama/rama-decor-flowers.png',
    'decor.hill.small':     'assets/platformer/rama/rama-decor-fence.png',
};

/**
 * Build the SkinResolver `elements` map by walking the tile + entity
 * catalogs from `platformerConfig.js`. Every slot the runtime asks
 * for (e.g. `platformer.tile.brick`, `platformer.entity.goomba`) is
 * declared here so OptionsScene's per-game tab can render an upload
 * widget for it. Footprints come from the catalog so multi-cell
 * tiles (pipes, hills, the flagpole) render at the right scale once
 * a PNG is set.
 */
function buildElements() {
    const elements = {
        'atlas.main': {
            label: 'Shared Atlas Source',
            category: 'atlas',
            footprint: { w: 1, h: 1 },
        },
        'background': {
            label: 'Sky / Background',
            category: 'board',
            fitMode: 'cover',
        },
    };

    // Stacked parallax background layers (Phase 4). Each is an
    // independent, importable image slot a stage can place at its own
    // parallax depth + auto-scroll speed. No shipped art — designers
    // import via the Content Browser; an unassigned layer simply draws
    // nothing. Six slots cover sky → far → mid → near comfortably.
    for (let i = 1; i <= 6; i++) {
        elements[`bg.${i}`] = {
            label: `Parallax BG ${i}`,
            category: 'board',
            fitMode: 'cover',
        };
    }

    // Autotile templates — one 4×4 sheet per terrain material. The
    // 16 cells map to the 4-bit neighbour mask (N=1 E=2 S=4 W=8,
    // index = mask, row-major) — see PlatformerAutotile.js for the
    // exact layout. When a template is imported, painting that
    // material automatically picks the right edge / corner / inner
    // cell; without one the material renders its single-tile art.
    for (const mat of ['ground', 'ice', 'sand']) {
        elements[`autotile.${mat}`] = {
            label: `Autotile ${cap(mat)} (4\u00d74 template)`,
            category: 'board',
            footprint: { w: 4, h: 4 },
        };
    }

    // Audio slots — short MP3 / WAV / OGG clips a designer can import
    // through the Content Browser and reference from Music Note elements
    // (or from area triggers with effect=sound). Eight reusable slots
    // are usually plenty: each one can power a whole synced note
    // sequence (e.g. a 5-second tune split into 5 × 1-second notes all
    // sharing audio.1). Slot src is a data URL persisted in the same
    // localStorage skin map as imported images.
    for (let i = 1; i <= 8; i++) {
        elements[`audio.${i}`] = {
            label: `Music / Sound ${i}`,
            category: 'audio',
        };
    }

    for (const [type, def] of Object.entries(PLATFORMER_TILE_DEFS)) {
        if (!def.slot) continue;   // slot-less defs carry no importable art
        const slotKey = def.slot.replace(/^platformer\./, ''); // 'tile.brick'
        elements[slotKey] = {
            label: prettyLabel(type),
            category: def.kind === 'tile' ? 'board' : 'obstacles',
            footprint: def.footprint || { w: 1, h: 1 },
            fallbackColor: def.fallbackColor,
        };
    }
    for (const [type, def] of Object.entries(PLATFORMER_ENTITY_DEFS)) {
        // Slot-less entities (area.trigger, fx.emitter) have no PNG to
        // import — they render procedurally — so skip them here.
        if (!def.slot) continue;
        const slotKey = def.slot.replace(/^platformer\./, ''); // 'entity.goomba'
        elements[slotKey] = {
            label: def.label,
            category: type === 'flag.goal' ? 'goal' :
                      type === 'coin' || type === 'mushroom' ? 'pickups' :
                      type === 'player.spawn' ? 'characters' :
                      type === 'checkpoint' ? 'goal' : 'characters',
            footprint: { w: 1, h: 1 },
            fallbackColor: def.fallbackColor,
        };
    }

    // Apply the shipped Rama theme defaults. User uploads override these.
    for (const [slot, path] of Object.entries(RAMA_ART)) {
        if (elements[slot]) elements[slot].defaultPath = path;
    }
    return elements;
}

function prettyLabel(type) {
    const parts = type.split('.');
    if (parts.length === 1) return cap(parts[0]);
    return `${cap(parts[0])}: ${parts.slice(1).map(cap).join(' ')}`;
}
function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

export const PLATFORMER = {
    grid: {
        cellSize: PLATFORMER_GRID.cellSize,
        cols:     PLATFORMER_GRID.cols,
        rows:     PLATFORMER_GRID.rows,
    },

    tuning: {
        gravity:              PLATFORMER_PHYSICS.gravity,
        terminalVelocity:     PLATFORMER_PHYSICS.terminalVelocity,
        walkSpeed:            PLATFORMER_PHYSICS.walkSpeed,
        runSpeed:             PLATFORMER_PHYSICS.runSpeed,
        friction:             PLATFORMER_PHYSICS.friction,
        airControl:           PLATFORMER_PHYSICS.airControl,
        jumpVelocity:         PLATFORMER_PHYSICS.jumpVelocity,
        jumpReleaseVelocity:  PLATFORMER_PHYSICS.jumpReleaseVelocity,
        coyoteFrames:         PLATFORMER_PHYSICS.coyoteFrames,
        jumpBufferFrames:     PLATFORMER_PHYSICS.jumpBufferFrames,
        enemySpeed:           PLATFORMER_PHYSICS.enemySpeed,
        stompBounce:          PLATFORMER_PHYSICS.stompBounce,
        startingLives:        PLATFORMER_PLAYER.startingLives,
        hitInvincibleFrames:  PLATFORMER_PLAYER.hitInvincibleFrames,
        pointsPerCoin:        PLATFORMER_SCORING.pointsPerCoin,
        pointsPerStomp:       PLATFORMER_SCORING.pointsPerStomp,
        pointsPerFlag:        PLATFORMER_SCORING.pointsPerFlag,
        pointsPerMushroom:    PLATFORMER_SCORING.pointsPerMushroom,
    },

    modes: {
        story:     { label: 'Story' },
        timeTrial: { label: 'Time Trial' },
    },

    settings: {
        gameMode: {
            default: 'story',
            type: 'choice',
            options: ['story', 'timeTrial'],
            label: 'Game Mode',
        },
        startingLives: {
            default: PLATFORMER_PLAYER.startingLives,
            type: 'number', min: 1, max: 9, step: 1,
            label: 'Starting Lives',
        },
        // How high the player jumps, expressed as the launch velocity
        // magnitude (px/frame). Higher = floatier, bigger leaps. The
        // default clears the 2-tile pipe blocks comfortably.
        jumpStrength: {
            default: Math.abs(PLATFORMER_PHYSICS.jumpVelocity),
            type: 'number',
            min: PLATFORMER_PHYSICS.jumpStrengthMin,
            max: PLATFORMER_PHYSICS.jumpStrengthMax,
            step: 0.5,
            label: 'Jump Strength',
        },
        startPower: {
            default: 'small',
            type: 'choice',
            options: ['small', 'super'],
            label: 'Starting Power',
        },
        // Time-trial countdown — only consumed when gameMode === timeTrial.
        // The default is generous; aggressive runs land near 60s.
        timeLimitSeconds: {
            default: 180,
            type: 'number', min: 30, max: 600, step: 10,
            label: 'Time Limit (s)',
        },
        infiniteLives: {
            default: false,
            type: 'boolean',
            label: 'Infinite Lives (Practice)',
        },
    },

    elements: buildElements(),

    palette: {
        sky:    0x6b9bd2,
        ground: 0xb45309,
        brick:  0xc2410c,
        pipe:   0x16a34a,
    },
};
